/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { Suspense, use, useCallback, useEffect, useMemo, useRef, useTransition } from 'react';
import { useAsyncError, useLocation, useNavigation, useRouteLoaderData } from 'react-router';
import type { loader as rootLoader } from '@/root';
import type { Route } from './+types/_app.category.$categoryId';
import type { ShopperProducts, ShopperSearch } from '@/scapi';
import { NormalizedApiError } from '@/lib/api/normalized-api-error';
import { fetchCategory } from '@/lib/api/categories.server';
import { fetchSearchProducts } from '@/lib/api/search.server';
import { getAllQueryParams, getQueryParam, PRODUCT_SEARCH_QUERY_PARAMS } from '@/lib/query-params';
import { getConfig, useConfig } from '@salesforce/storefront-next-runtime/config';
import { siteContext } from '@salesforce/storefront-next-runtime/site-context';
import CategoryBreadcrumbs from '@/components/category-breadcrumbs';
import CategoryPagination from '@/components/category-pagination';
import LoadMore from '@/components/product-grid/load-more';
import { useLoadMoreProducts } from '@/hooks/use-load-more-products';
import ActiveFilters from '@/components/category-refinements/active-filters';
import FiltersButton from '@/components/category-refinements/filters-button';
import CategoryRefinements from '@/components/category-refinements';
import CategorySorting from '@/components/category-sorting';
import DeferredProductGrid from '@/components/product-grid';
import { ProductTileSkeleton } from '@/components/category-skeleton';
import QuickFilters from '@/components/quick-filters';
import { useAnalytics } from '@/hooks/use-analytics';
import { PageType } from '@/lib/decorators/page-type';

import { JsonLd } from '@/components/json-ld';
import { SeoMeta } from '@/components/seo-meta';
import { useTranslation } from 'react-i18next';
import { UITarget } from '@/targets/ui-target';
import { generateCategorySchema } from '@/utils/category-schema';
import { getPublicOrigin } from '@/utils/schema-url';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import {
    getInitialFiltersOpen,
    getSearchWithoutClientOnlyParams,
    getSearchWithoutFiltersParam,
    useFiltersPanelState,
} from '@/hooks/use-filters-panel-state';
import { getLogger } from '@/lib/logger.server';
import { uiConfig } from '@/lib/config.ui';
import { Region } from '@/components/region';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { fetchPageWithComponentData } from '@/lib/page-designer/page-loader.server';
// RGBARRY-CUSTOMIZATION: brand PLP extras — Shop by Style bar + category image tiles
import { toImageUrl } from '@/lib/images/dynamic-image';
import CategoryImageFilters, { type CategoryImageTile } from '@/components/category-image-filters';
import { Link } from '@/components/link';
import { routes, routeHref } from '@/route-paths';

// RGBARRY-CUSTOMIZATION: "Shop by Style" bar — one product image per subcategory, shown only on
// the top-level "Shop Bags" category. Data is fetched as a deferred promise in the loader below.
const STYLE_BAR_CATEGORY_ID = '35';

type StyleBarItem = {
    categoryId: string;
    label: string;
    imageUrl: string | null;
};

function ShopByStyleBarInner({ dataPromise }: { dataPromise: Promise<StyleBarItem[]> }) {
    const items = use(dataPromise);
    if (!items.length) return null;
    return (
        <div className="mb-8 -mx-4 sm:-mx-8 lg:-mx-16 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex gap-1 px-4 sm:px-8 lg:px-16 min-w-max">
                {items.map((item) => (
                    <Link
                        key={item.categoryId}
                        to={routeHref(routes.category, { categoryId: item.categoryId })}
                        className="group flex flex-col items-center gap-3 w-[120px] sm:w-[140px] shrink-0 py-4 px-2 hover:bg-white transition-colors duration-150">
                        <div className="w-[88px] h-[88px] sm:w-[104px] sm:h-[104px] bg-[#F4F4F4] overflow-hidden flex items-center justify-center">
                            {item.imageUrl ? (
                                <img
                                    src={item.imageUrl}
                                    alt={item.label}
                                    className="w-full h-full object-contain mix-blend-multiply"
                                    loading="lazy"
                                />
                            ) : (
                                <div className="w-full h-full bg-[#ececec]" />
                            )}
                        </div>
                        <span className="text-[11px] font-semibold tracking-[0.08em] uppercase text-center text-[#1a1a1a] group-hover:underline leading-tight">
                            {item.label}
                        </span>
                    </Link>
                ))}
            </div>
        </div>
    );
}

function ShopByStyleBar({ dataPromise }: { dataPromise: Promise<StyleBarItem[]> | null }) {
    if (!dataPromise) return null;
    return (
        <Suspense fallback={null}>
            <ShopByStyleBarInner dataPromise={dataPromise} />
        </Suspense>
    );
}

// RGBARRY-CUSTOMIZATION: image tiles shown at the top of selected category pages, keyed by category ID.
const CATEGORY_IMAGE_TILES: Record<string, CategoryImageTile[]> = {
    'shop-bags': [
        {
            categoryId: 'crossbody-handbags',
            label: 'Crossbody',
            imageUrl:
                'https://www.baggallini.com/on/demandware.static/-/Sites/default/dwce2f96bd/nav-image-manhattan-2.jpg',
        },
        {
            categoryId: 'womens-backpacks',
            label: 'Backpacks',
            imageUrl:
                'https://www.baggallini.com/on/demandware.static/-/Sites/default/dw07c4587c/nav-image-moderneverywhere.jpg',
        },
        {
            categoryId: 'shop-all-travel',
            label: 'Travel',
            imageUrl: 'https://www.baggallini.com/on/demandware.static/-/Sites/default/dwb04eba30/nav-image-travel.jpg',
        },
        {
            categoryId: 'jet-set',
            label: 'Jet Set',
            imageUrl: 'https://www.baggallini.com/on/demandware.static/-/Sites/default/dw48a2554a/jet-set-menu-nav.jpg',
        },
    ],
    womens: [
        {
            categoryId: 'crossbody-handbags',
            label: 'Crossbody',
            imageUrl:
                'https://www.baggallini.com/on/demandware.static/-/Sites/default/dwce2f96bd/nav-image-manhattan-2.jpg',
        },
        {
            categoryId: 'womens-backpacks',
            label: 'Backpacks',
            imageUrl:
                'https://www.baggallini.com/on/demandware.static/-/Sites/default/dw07c4587c/nav-image-moderneverywhere.jpg',
        },
        {
            categoryId: 'shop-all-travel',
            label: 'Travel',
            imageUrl: 'https://www.baggallini.com/on/demandware.static/-/Sites/default/dwb04eba30/nav-image-travel.jpg',
        },
        {
            categoryId: 'jet-set',
            label: 'Jet Set',
            imageUrl: 'https://www.baggallini.com/on/demandware.static/-/Sites/default/dw48a2554a/jet-set-menu-nav.jpg',
        },
    ],
};

@PageType({
    name: 'Product Listing Page',
    description: 'Product listing page with product listings and personalized content',
    supportedAspectTypes: ['plp'],
})
@RegionDefinition([
    {
        id: 'plpTopFullWidth',
        name: 'Top Full Width Region',
        description: 'Full screen width region at the top of the results, below the header and above the breadcrumb',
        maxComponents: 5,
    },
])
export class ProductListingPageMetadata {}

type CategoryPageData = {
    category: ShopperProducts.schemas['Category'];
    searchResultCritical: ShopperSearch.schemas['ProductSearchResult'];
    searchResultNonCritical: Promise<ShopperSearch.schemas['ProductSearchResult']>;
    categoryId: string;
    pageUrl: string;
    refine: string[];
    currency: string;
    locale: string;
    initialFiltersOpen?: boolean;
    categorySchema: Promise<ReturnType<typeof generateCategorySchema> | null>;
    /** `rel=prev/next` crawl URLs for load-more mode, or `null` in traditional mode. */
    seoPagination: { prevUrl: string | null; nextUrl: string | null } | null;
    /** Products the server rendered on first paint. */
    initialCount: number;
    /** Server-side offset for `?page=N` entry in load-more mode (0 for normal entry). */
    offset?: number;
    /** RGBARRY-CUSTOMIZATION: deferred Shop-by-Style bar data (null except on the style-bar category). */
    styleBarData?: Promise<StyleBarItem[]> | null;
    /** RGBARRY-CUSTOMIZATION: PD plpTopFullWidth zone. Awaited in the loader (blocks the grid) so
     *  it never pops in after first paint — but raced against a short timeout guard so a
     *  slow/unavailable PD fetch degrades to empty instead of holding up the whole PLP past the
     *  entry.server.tsx stream abort (see removal history in commit 1a6b7a9). */
    page?: Awaited<ReturnType<typeof fetchPageWithComponentData>>;
};

// RGBARRY-CUSTOMIZATION: brand PLP renders on a white surface, pulled up under the header.
// RGBARRY-CUSTOMIZATION: `analytics.routeType` classifies this route for the GTM data layer's
// page-view event (see src/analytics/route-analytics.ts). Route-based, so it survives the legacy
// bare-slug PLP URL shape that no path-prefix blocklist could match.
export const handle = {
    ui: { main: { backgroundClass: 'bg-white' } },
    analytics: { routeType: 'category' },
};

/**
 * Server-side loader function that fetches category data and product search results.
 * This function runs on the server during SSR and prepares data for the category page.
 * @returns Object containing search results, category data, and page metadata
 */
export async function loader(args: Route.LoaderArgs): Promise<CategoryPageData> {
    const {
        context,
        request,
        params: { categoryId },
    } = args;
    const requestUrl = new URL(request.url);
    const { searchParams } = requestUrl;
    const logger = getLogger(context);
    logger.debug('Category: loader starting', {
        categoryId,
        offset: parseInt(searchParams.get('offset') || '0', 10),
    });
    const sort = getQueryParam(searchParams, PRODUCT_SEARCH_QUERY_PARAMS.SORT);
    const refine = getAllQueryParams(searchParams, PRODUCT_SEARCH_QUERY_PARAMS.REFINE);
    const initialFiltersOpen = getInitialFiltersOpen(searchParams);

    // Get currency and locale for cache-busting the page key
    const config = getConfig(context);
    const siteCtx = context.get(siteContext);
    if (!siteCtx) {
        logger.error('Category: site context is not available');
        throw new Response('Site context is not available', { status: 500 });
    }
    const { currency } = siteCtx;
    const locale = siteCtx.locale.id;
    const limit = config.search.products.hits.limit;

    // Pagination params. Which ones are honored depends on the merchant's pagination mode:
    // - `traditional`: `?offset=N` drives the numbered page window (existing behavior).
    // - `load-more`: the client persists loaded count to sessionStorage for back-nav restoration;
    //   `?page=N` is the crawler/no-JS fallback that server-renders a single traditional page window.
    const paginationConfig = uiConfig.pages.category.pagination;
    const isLoadMoreMode = paginationConfig.mode === 'load-more';
    const requestedOffset = parseInt(getQueryParam(searchParams, PRODUCT_SEARCH_QUERY_PARAMS.OFFSET) || '0', 10) || 0;
    const requestedPage = parseInt(searchParams.get('page') || '0', 10) || 0;
    // In load-more mode a `?page=N` request is the SEO/no-JS path — serve that page's offset window.
    const isPagedRequest = isLoadMoreMode && requestedPage > 1;
    const offset = isPagedRequest ? (requestedPage - 1) * limit : requestedOffset;
    const initialFetchLimit = limit;

    // RGBARRY-CUSTOMIZATION: PD plpTopFullWidth zone must render together with the grid,
    // not pop in after first paint (a pop-in here is a real CLS hit — see removal history in commit
    // 1a6b7a9 for why this fetch must never be allowed to hang the whole route). Kicked off here,
    // concurrently with fetchCategory/fetchSearchProducts below, and awaited just before return so
    // its latency overlaps with theirs instead of adding on top. Raced against a short timeout guard
    // — much shorter than the old deferred-era 9s ceiling, since it now blocks the entire PLP grid
    // rather than just its own Suspense boundary. 800ms is a starting point pending real staging
    // latency data from the timing log below; tune once the MRT Data Store hit rate is known.
    const pdFetchStart = performance.now();
    let pdTimedOut = false;
    let pdTimeoutId: ReturnType<typeof setTimeout>;
    const pdPagePromise = Promise.race([
        fetchPageWithComponentData(args, { aspectType: 'plp', categoryId })
            .catch((e): null => {
                getLogger(context).error('Category PLP Page Designer fetch failed; rendering PD zones empty', {
                    categoryId,
                    error: e instanceof Error ? e.message : String(e),
                });
                return null;
            })
            .finally(() => clearTimeout(pdTimeoutId)),
        new Promise<null>((resolve) => {
            pdTimeoutId = setTimeout(() => {
                pdTimedOut = true;
                resolve(null);
            }, 800);
        }),
    ]);

    let categoryData: ShopperProducts.schemas['Category'] | undefined;
    try {
        categoryData = await fetchCategory(context, categoryId, 1);
    } catch (e) {
        if (e instanceof NormalizedApiError && e.status) {
            throw new Response(e.message, { status: e.status });
        }
        throw new Response('Internal Server Error', { status: 500 });
    }

    // Keep non-category refinements and apply exactly one category refinement.
    // If URL already contains a cgid refine (e.g. from quick filters), honor it.
    // Otherwise, default to the category id from the route path.
    const effectiveRefine = refine.filter((r) => !r.startsWith('cgid='));
    const selectedCgidRefine = refine.find((r) => r.startsWith('cgid='));
    effectiveRefine.push(selectedCgidRefine ?? `cgid=${categoryId}`);

    // Ensure criticalCount doesn't exceed limit to prevent negative non-critical limit
    const criticalCount = config.search.products.hits.critical ?? 4;
    const safeCriticalCount = Math.min(criticalCount, limit);
    // RGBARRY-CUSTOMIZATION: guard the awaited critical search. A SCAPI ProductSearch fault (e.g. the
    // platform "could not be processed within the timeout threshold" on an uncached heavy query) must
    // not take down the whole PLP via the route error boundary. Degrade to an empty result so the page
    // shell, breadcrumbs and refinements still render; the non-critical batch still streams below.
    let searchResultCritical: ShopperSearch.schemas['ProductSearchResult'];
    try {
        searchResultCritical = await fetchSearchProducts(context, {
            limit: safeCriticalCount,
            offset,
            sort,
            refine: effectiveRefine,
            currency,
        });
    } catch (e) {
        getLogger(context).error('Category PLP critical product search failed; rendering empty result', {
            categoryId,
            error: e instanceof Error ? e.message : String(e),
        });
        searchResultCritical = {
            limit: safeCriticalCount,
            hits: [],
            query: '',
            refinements: [],
            sortingOptions: [],
            searchPhraseSuggestions: {},
            offset,
            total: 0,
        } as unknown as ShopperSearch.schemas['ProductSearchResult'];
    }

    const effectiveCriticalCount = searchResultCritical.hits?.length ?? 0;
    // RGBARRY-CUSTOMIZATION: the non-critical batch is a DEFERRED promise returned in loader data and
    // streamed by React Router. It must NEVER reject — a rejection here escapes as a server-side
    // unhandled rejection during SSR streaming and crashes the whole PLP via the root error boundary
    // ("Something went wrong / TypeError: network error"), even though the product grid has its own
    // errorElement (that boundary only catches the *derived* promise the grid consumes, not this raw
    // one). The prior Promise.race only guarded SLOW responses (9s timeout); a FAST rejection — which is
    // exactly what a cold, expensive shop-all query (New-Arrivals) produces when the SCAPI search faults
    // or the platform trips its internal timeout — settled the race with a rejection before the timeout
    // could fire. Wrapping the fetch in `.catch()` (resolve-to-empty, same shape as the timeout branch)
    // closes that hole: the page shell, breadcrumbs, refinements and critical hits still render, and the
    // grid simply shows no additional rows instead of the whole route crashing.
    const emptyNonCriticalResult = { ...searchResultCritical, hits: [] };
    let nonCriticalTimeoutId: ReturnType<typeof setTimeout>;
    const searchResultNonCritical = Promise.race([
        fetchSearchProducts(context, {
            // On a `?loaded=N` restoration this widens to N so the full run is server-rendered; otherwise
            // it's the normal single-page window.
            limit: initialFetchLimit - effectiveCriticalCount,
            offset: offset + effectiveCriticalCount,
            sort,
            refine: effectiveRefine,
            currency,
        })
            .catch((e): ShopperSearch.schemas['ProductSearchResult'] => {
                getLogger(context).error('Category PLP non-critical product search failed; streaming empty result', {
                    categoryId,
                    error: e instanceof Error ? e.message : String(e),
                });
                return emptyNonCriticalResult;
            })
            .finally(() => clearTimeout(nonCriticalTimeoutId)),
        new Promise<ShopperSearch.schemas['ProductSearchResult']>((resolve) => {
            // RGBARRY-CUSTOMIZATION: 9s deadline (was 4s) sits just under the 11s stream abort, giving the
            // non-critical batch room to arrive on slow sandbox latency instead of degrading to empty hits.
            nonCriticalTimeoutId = setTimeout(() => resolve(emptyNonCriticalResult), 9_000);
        }),
    ]);

    const pageUrl = buildCanonicalUrl(requestUrl.origin, requestUrl.pathname, requestUrl.search);

    // RGBARRY-CUSTOMIZATION: build the Shop by Style bar — one product image per subcategory, fired in
    // parallel as a deferred promise. Only for the top-level "Shop Bags" category; null everywhere else.
    // RGBARRY-CUSTOMIZATION: race against 4s deadline so N subcategory SCAPI calls can't hold the
    // stream open past the 5s abort in entry.server.tsx and crash the Shop Bags category page.
    const styleBarData: Promise<StyleBarItem[]> | null =
        categoryId === STYLE_BAR_CATEGORY_ID &&
        Array.isArray(categoryData.categories) &&
        categoryData.categories.length > 0
            ? Promise.race([
                  Promise.all(
                      (categoryData.categories as ShopperProducts.schemas['Category'][])
                          .filter((sub) => sub.id && sub.name)
                          .map(async (sub): Promise<StyleBarItem> => {
                              try {
                                  const result = await fetchSearchProducts(context, {
                                      limit: 1,
                                      offset: 0,
                                      refine: [`cgid=${sub.id}`],
                                      currency,
                                  });
                                  const hit = result.hits?.[0];
                                  type ImgObj = { disBaseLink?: string; link?: string };
                                  // Try imageGroups['hi-res'] first, then 'large', then hit.image fallback
                                  const hiResGroup =
                                      hit?.imageGroups?.find((g: { viewType?: string }) => g.viewType === 'hi-res') ??
                                      hit?.imageGroups?.find((g: { viewType?: string }) => g.viewType === 'large') ??
                                      hit?.imageGroups?.[0];
                                  const firstImg = (hiResGroup as { images?: ImgObj[] } | undefined)?.images?.[0];
                                  const rawUrl =
                                      firstImg?.disBaseLink ??
                                      firstImg?.link ??
                                      (hit?.image as ImgObj | undefined)?.disBaseLink ??
                                      (hit?.image as ImgObj | undefined)?.link ??
                                      null;
                                  // Run through the same image pipeline as PLP/PDP so embedded
                                  // Scene7 URLs are unwrapped and DIS params are applied correctly.
                                  const imageUrl = rawUrl ? (toImageUrl({ src: rawUrl, config }) ?? rawUrl) : null;
                                  return {
                                      categoryId: sub.id,
                                      label: sub.name ?? sub.id,
                                      imageUrl,
                                  };
                              } catch {
                                  return { categoryId: sub.id, label: sub.name ?? sub.id, imageUrl: null };
                              }
                          })
                  ),
                  new Promise<StyleBarItem[]>((resolve) => setTimeout(() => resolve([]), 4_000)),
              ])
            : null;

    // RGBARRY-CUSTOMIZATION: await the PD fetch kicked off at the top of this loader (see comment
    // there) now that its latency has already been overlapping with fetchCategory/fetchSearchProducts
    // above. Logged here (rather than inside the .then() chain) so the duration reflects the actual
    // added-latency-to-TTFB now that this is blocking, not just informational.
    const page = await pdPagePromise;
    getLogger(context).debug('Category PLP Page Designer fetch timing', {
        categoryId,
        durationMs: Math.round(performance.now() - pdFetchStart),
        timedOut: pdTimedOut,
        hasContent: !!page,
    });

    // SEO / crawler pagination (load-more mode only). Expose `rel=prev/next` URLs so bots can discover
    // and crawl the full result set via `?page=N` even though shoppers use the JS "load more" flow.
    // These use the clean path + a single `page` param (canonical stays the base URL; see SeoMeta).
    const totalCount = searchResultCritical.total ?? 0;
    const totalPages = Math.max(1, Math.ceil(totalCount / limit));
    const currentPage = isPagedRequest ? requestedPage : 1;
    const buildPageUrl = (p: number): string => {
        const params = new URLSearchParams();
        for (const r of refine) params.append('refine', r);
        if (sort) params.set('sort', sort);
        if (p > 1) params.set('page', String(p));
        const qs = params.toString();
        return `${requestUrl.origin}${requestUrl.pathname}${qs ? `?${qs}` : ''}`;
    };
    const seoPagination = isLoadMoreMode
        ? {
              prevUrl: currentPage > 1 ? buildPageUrl(currentPage - 1) : null,
              nextUrl: currentPage < totalPages ? buildPageUrl(currentPage + 1) : null,
          }
        : null;

    // Generate category schema in loader (server-side) for SEO
    const categorySchemaPromise = searchResultNonCritical
        .then((searchResult: ShopperSearch.schemas['ProductSearchResult']) => {
            try {
                // Use public origin from request headers instead of request.url
                // to avoid exposing internal AWS Lambda URLs in schema
                const publicOrigin = getPublicOrigin(request);
                const url = new URL(request.url);
                const schemaPageUrl = `${publicOrigin}${url.pathname}${url.search}`;
                // Validate inputs before generating schema
                if (!categoryData || !searchResult) {
                    return null;
                }
                return generateCategorySchema({
                    category: categoryData,
                    searchResult: {
                        ...searchResult,
                        hits: [...(searchResultCritical.hits || []), ...(searchResult.hits || [])],
                    },
                    config,
                    pageUrl: schemaPageUrl,
                    defaultCurrency: currency,
                });
            } catch (error) {
                logger.error('Error generating category schema in loader', {
                    error,
                });
                return null;
            }
        })
        .catch((error) => {
            logger.error('Error in category schema promise chain', {
                error,
            });
            return null;
        });

    return {
        category: categoryData,
        searchResultCritical,
        searchResultNonCritical,
        categoryId,
        pageUrl,
        refine: effectiveRefine,
        currency,
        locale,
        initialFiltersOpen,
        categorySchema: categorySchemaPromise,
        seoPagination,
        initialCount: Math.min(initialFetchLimit, Math.max(0, (searchResultCritical.total ?? 0) - offset)),
        offset,
        styleBarData,
        page,
    };
}

export { shouldRevalidate } from '@/lib/revalidation/routes/category';

/**
 * Category page component that displays a product category with filtering, sorting, and pagination.
 * This component uses the createPage factory to handle Suspense patterns.
 * @returns JSX element representing the category page
 */
function ProductGridError() {
    const rawError = useAsyncError();
    const error = rawError instanceof NormalizedApiError ? rawError : null;
    const { t } = useTranslation('common');
    return (
        <div role="alert" className="col-span-full py-8 text-center text-muted-foreground">
            <p>{t('productGrid.loadFailed')}</p>
            {import.meta.env.DEV && error && (
                <div className="mt-2 text-xs font-mono text-muted-foreground/70">
                    {error.status && <span>{error.status} </span>}
                    {error.message && <p>{error.message}</p>}
                </div>
            )}
        </div>
    );
}

/**
 * Component that renders JSON-LD schema when categorySchema promise resolves.
 * Must be inside Suspense boundary to ensure it streams correctly in SSR.
 */
function CategoryJsonLd({
    categorySchemaPromise,
}: {
    categorySchemaPromise: Promise<ReturnType<typeof generateCategorySchema> | null>;
}) {
    const categorySchema = use(categorySchemaPromise);
    const rootData = useRouteLoaderData<typeof rootLoader>('root');
    const nonce = rootData?.nonce ?? undefined;
    return categorySchema ? <JsonLd data={categorySchema} id="category-schema" nonce={nonce} /> : null;
}

export default function CategoryPage({
    loaderData: {
        category,
        searchResultCritical,
        searchResultNonCritical,
        categoryId,
        pageUrl,
        refine,
        locale,
        currency,
        initialFiltersOpen,
        categorySchema,
        seoPagination,
        initialCount,
        offset = 0,
        styleBarData = null,
        page = null,
    },
}: {
    loaderData: CategoryPageData;
}) {
    const config = useConfig();
    const { t } = useTranslation();

    const [filtersOpen, toggleFiltersOpen] = useFiltersPanelState(initialFiltersOpen);
    const limit = config.search.products.hits.limit;

    // Determine the maximum number of skeletons to display in the product grid.
    // Out-of-the-box the idea is to not display more than 8 skeletons, i.e., two rows on a desktop device.
    // Wrap in Math.max(0, ...) to prevent negative values when criticalCount is high(er).
    const criticalCount = searchResultCritical.hits?.length ?? 0;
    const nonCriticalCount = Math.max(
        0,
        Math.min(8, limit, searchResultCritical.total - searchResultCritical.offset) - criticalCount
    );

    const analytics = useAnalytics();
    const lastTrackedDataRef = useRef<string | null>(null);
    const resultsHeadingRef = useRef<HTMLHeadingElement>(null);

    const location = useLocation();
    const navigation = useNavigation();
    const searchWithoutFiltersParam = useMemo(() => getSearchWithoutFiltersParam(location.search), [location.search]);
    const pageIdentity = `${categoryId}-${currency}-${locale}`;
    const analyticsKey = `${pageIdentity}-${searchWithoutFiltersParam}-${location.hash}`;
    const productGridDataKey = `${pageIdentity}-${searchWithoutFiltersParam}`;
    const selectedFiltersCount = useMemo(
        () => new URLSearchParams(location.search).getAll('refine').length,
        [location.search]
    );

    // QuickFilters "Shop by {label}" header is opt-in per build target via
    // `uiConfig.pages.category.showCategoryLabel`. When on, derive the label from
    // the active `cgid` refinement and pass it down; when off, pass nothing so
    // QuickFilters renders the chips-only baseline. Keeping this in the (shared)
    // route means QuickFilters stays presentational and no vertical has to fork
    // either the component or this route. See @/lib/config.ui.
    const categoryLabel = uiConfig.pages.category.showCategoryLabel
        ? searchResultCritical.refinements?.find((r) => r.attributeId === 'cgid')?.label
        : undefined;
    const isProductGridLoading = useMemo(() => {
        if (navigation.state === 'idle' || !navigation.location) {
            return false;
        }
        const currentRefines = new URLSearchParams(location.search).getAll('refine');
        const nextRefines = new URLSearchParams(navigation.location.search).getAll('refine');
        return (
            currentRefines.length !== nextRefines.length ||
            currentRefines.some((currentRefine, index) => currentRefine !== nextRefines[index])
        );
    }, [location.search, navigation.location, navigation.state]);

    const nonCriticalPromise = useMemo(
        () => searchResultNonCritical.then((r) => r.hits ?? []),
        [searchResultNonCritical]
    );

    // Pagination mode is merchant-configurable: `load-more` (button + infinite scroll, the default)
    // or `traditional` (numbered prev/next that navigates the URL offset). See @/lib/config.ui.
    const paginationConfig = uiConfig.pages.category.pagination;
    const isLoadMoreMode = paginationConfig.mode === 'load-more';

    // "Load more" / infinite scroll: the loader renders the first page (`initialCount` products) and
    // this hook appends further pages via a non-navigating fetch, resetting whenever the underlying
    // query changes (category / sort / refinements). On bfcache-miss back-nav, the hook reads
    // `sfnextLoaded` from history.state and auto-fetches to restore the prior scroll depth.
    const {
        appended,
        loadedCount,
        total: loadMoreTotal,
        hasMore,
        capReached,
        isLoading: isLoadingMore,
        isRestoring,
        restorationTarget,
        hasError: loadMoreError,
        firstNewIndex,
        loadMore,
    } = useLoadMoreProducts({
        refine,
        sort: searchResultCritical.selectedSortingOption,
        currency,
        initialCount,
        criticalCount,
        nonCriticalHits: nonCriticalPromise,
        total: searchResultCritical.total,
        batchSize: paginationConfig.batchSize,
        mobileBatchSize: paginationConfig.mobileBatchSize,
        maxProducts: paginationConfig.maxProducts,
        identity: productGridDataKey,
        offset,
    });

    // Accessibility: after a "load more" appends a batch, move focus to its first tile so keyboard and
    // screen-reader users land on the new content instead of staying on the (now mid-page) button.
    const firstNewItemRef = useRef<HTMLDivElement>(null);
    const focusTargetKey = firstNewIndex === null ? null : `${appended.length}-${firstNewIndex}`;
    useEffect(() => {
        if (focusTargetKey === null || !firstNewItemRef.current) {
            return;
        }
        const node = firstNewItemRef.current;
        // ProductTile's root isn't natively focusable; make it programmatically focusable, focus it,
        // then drop the tabindex on blur so it doesn't linger in the tab order.
        node.setAttribute('tabindex', '-1');
        node.focus({ preventScroll: true });
        const onBlur = () => node.removeAttribute('tabindex');
        node.addEventListener('blur', onBlur, { once: true });
        return () => node.removeEventListener('blur', onBlur);
    }, [focusTargetKey]);

    // Persist loaded count in sessionStorage keyed by React Router's history key. React Router
    // overwrites history.state with { key } on navigation, so history.state is not safe for custom
    // data. sessionStorage survives SPA navigations and is the same mechanism React Router uses
    // for its own scroll positions. URL stays clean — no params exposed to users.
    const STORAGE_KEY = 'sfnext:loadMore';
    useEffect(() => {
        if (!isLoadMoreMode || typeof window === 'undefined' || isRestoring) {
            return;
        }
        const historyKey = (window.history.state as { key?: string } | null)?.key;
        if (!historyKey) return;
        try {
            if (loadedCount > initialCount) {
                const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
                stored[historyKey] = loadedCount;
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
            } else {
                const stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '{}');
                delete stored[historyKey];
                sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
            }
        } catch {
            // sessionStorage full or unavailable — non-critical, skip
        }
    }, [isLoadMoreMode, isRestoring, loadedCount, initialCount]);

    const [, startTransition] = useTransition();
    // Compare on the meaningful search only. Opening/closing the filters panel writes a client-only
    // `filters` param (see use-filters-panel-state); that toggle must not steal focus up to the
    // heading — the panel manages its own focus. (W-23325653)
    const lastSearchParamsRef = useRef<string>(getSearchWithoutClientOnlyParams(location.search));

    useEffect(() => {
        // Move focus to results heading after refinement or sort changes
        const meaningfulSearch = getSearchWithoutClientOnlyParams(location.search);
        if (navigation.state === 'idle' && lastSearchParamsRef.current !== meaningfulSearch) {
            lastSearchParamsRef.current = meaningfulSearch;
            // Allow the DOM to update before moving focus
            requestAnimationFrame(() => {
                resultsHeadingRef.current?.focus();
            });
        }
    }, [navigation.state, location.search]);

    // When the shopper opens the filters panel, move focus into it so keyboard users land on the
    // refinements instead of tabbing forward from a toggle that sits after the panel in the DOM.
    // Only fire on the closed -> open transition, never on initial render. (W-23325653)
    const refinementsPanelRef = useRef<HTMLDivElement>(null);
    const prevFiltersOpenRef = useRef(filtersOpen);
    useEffect(() => {
        if (filtersOpen && !prevFiltersOpenRef.current) {
            requestAnimationFrame(() => {
                refinementsPanelRef.current?.focus();
            });
        }
        prevFiltersOpenRef.current = filtersOpen;
    }, [filtersOpen]);

    useEffect(() => {
        // Only track if we haven't already tracked this specific data combination
        if (analyticsKey !== lastTrackedDataRef.current) {
            lastTrackedDataRef.current = analyticsKey;

            startTransition(() => {
                void nonCriticalPromise
                    .then((searchHitsData: ShopperSearch.schemas['ProductSearchHit'][]) => {
                        if (analytics) {
                            void analytics.trackViewCategory({
                                category,
                                searchResults: [...(searchResultCritical.hits ?? []), ...searchHitsData],
                                sort:
                                    searchResultCritical.selectedSortingOption ||
                                    searchResultCritical.sortingOptions?.[0]?.label ||
                                    '',
                                refinements: searchResultCritical.selectedRefinements ?? {},
                                offset: searchResultCritical.offset,
                                limit,
                                total: searchResultCritical.total,
                            });
                        }
                    })
                    .catch(() => {
                        // Silently handle promise rejection
                    });
            });
        }
        // oxlint-disable-next-line react-hooks/exhaustive-deps
    }, [analytics, category, analyticsKey, nonCriticalPromise]);

    const handleProductClick = useCallback(
        (product: ShopperSearch.schemas['ProductSearchHit']) => {
            if (analytics) {
                void analytics.trackClickProductInCategory({
                    category,
                    product,
                });
            }
        },
        [analytics, category]
    );

    return (
        <>
            <SeoMeta
                title={category.name || category.id}
                description={category.pageDescription || category.description}
                openGraph={{
                    type: 'website',
                    url: pageUrl,
                }}
            />
            {/* SEO pagination hints: let crawlers walk the full result set via `?page=N` even though
                shoppers use the JS "load more" flow. React 19 hoists these <link>s into <head>. */}
            {seoPagination?.prevUrl && <link rel="prev" href={seoPagination.prevUrl} />}
            {seoPagination?.nextUrl && <link rel="next" href={seoPagination.nextUrl} />}
            <div className="pb-16 -mt-8">
                {/* RGBARRY-CUSTOMIZATION: plpTopFullWidth — full-width PD zone below the header,
                    above the breadcrumb. Omitting fallbackElement/errorElement is intentional: this
                    region is optional editorial content, so an empty/unconfigured PD page should
                    render nothing rather than a hardcoded fallback (see docs/README-PAGE-DESIGNER.md
                    "Error Fallbacks: Anti-Pattern"). */}
                <Region page={page} regionId="plpTopFullWidth" />

                <div className="section-container pt-8">
                    <div className="mb-4">
                        <CategoryBreadcrumbs category={category} />
                    </div>

                    {/* RGBARRY-CUSTOMIZATION: Shop by Style bar (top-level "Shop Bags" only) */}
                    <ShopByStyleBar dataPromise={styleBarData} />

                    <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        {/* RGBARRY-CUSTOMIZATION: heading shows a subdued item count instead of "(N)" */}
                        <h1
                            ref={resultsHeadingRef}
                            tabIndex={-1}
                            className="text-3xl font-bold leading-none tracking-[-0.75px] text-card-foreground flex items-baseline gap-3 rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2">
                            <span>{category?.name || category.id}</span>
                            <span className="text-sm font-normal tracking-normal text-muted-foreground">
                                {searchResultCritical.total} {searchResultCritical.total === 1 ? 'item' : 'items'}
                            </span>
                        </h1>
                        <UITarget targetId="sfcc.plp.search.summary" />
                        {searchResultCritical?.sortingOptions && searchResultCritical.sortingOptions.length > 0 && (
                            <div className="hidden lg:block flex-shrink-0">
                                <CategorySorting result={searchResultCritical} />
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col lg:flex-row gap-2">
                        {/* RGBARRY-CUSTOMIZATION: Filters + Sort on one row for mobile */}
                        {/* Filters toggle button + Quick Filters - mobile only (above panel) */}
                        <div className="lg:hidden mb-4 flex flex-col items-start gap-2" data-slot="filters-wrapper">
                            <div className="flex w-full items-center justify-between gap-2">
                                <FiltersButton
                                    onClick={toggleFiltersOpen}
                                    isActive={filtersOpen}
                                    selectedFiltersCount={selectedFiltersCount}
                                />
                                {searchResultCritical?.sortingOptions &&
                                    searchResultCritical.sortingOptions.length > 0 && (
                                        <CategorySorting result={searchResultCritical} />
                                    )}
                            </div>
                            <QuickFilters category={category} categoryLabel={categoryLabel} />
                        </div>

                        {/* Category Refinements - toggles visibility on left side */}
                        {filtersOpen && (
                            <div
                                ref={refinementsPanelRef}
                                tabIndex={-1}
                                role="region"
                                aria-label={t('categoryRefinements:filtersButtonLabel')}
                                className="w-full lg:w-64 lg:flex-shrink-0 outline-none">
                                <CategoryRefinements result={searchResultCritical} refine={refine} />
                            </div>
                        )}

                        <div className="flex-grow">
                            {/* Filters toggle button + Quick Filters - desktop only (inside content area) */}
                            <div className="mb-4 hidden lg:flex lg:items-center lg:gap-4" data-slot="filters-wrapper">
                                <FiltersButton
                                    onClick={toggleFiltersOpen}
                                    isActive={filtersOpen}
                                    selectedFiltersCount={selectedFiltersCount}
                                />
                                <QuickFilters category={category} categoryLabel={categoryLabel} />
                            </div>

                            {/* RGBARRY-CUSTOMIZATION: category image tiles for select categories */}
                            {CATEGORY_IMAGE_TILES[categoryId] && (
                                <CategoryImageFilters tiles={CATEGORY_IMAGE_TILES[categoryId]} />
                            )}

                            <ActiveFilters result={searchResultCritical} />

                            <UITarget targetId="sfcc.plp.agent.categoryHelper" />
                            <UITarget targetId="sfcc.plp.search.results">
                                {isRestoring ? (
                                    <div
                                        className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-x-4 gap-y-8"
                                        aria-busy="true">
                                        {Array.from({ length: restorationTarget }, (_, i) => (
                                            <ProductTileSkeleton key={`restore-skeleton-${i}`} />
                                        ))}
                                    </div>
                                ) : (
                                    <DeferredProductGrid
                                        key={productGridDataKey}
                                        critical={searchResultCritical.hits ?? []}
                                        nonCritical={nonCriticalPromise}
                                        appended={isLoadMoreMode ? appended : undefined}
                                        firstNewIndex={isLoadMoreMode ? firstNewIndex : undefined}
                                        firstNewItemRef={isLoadMoreMode ? firstNewItemRef : undefined}
                                        appendPending={isLoadMoreMode ? isLoadingMore : undefined}
                                        nonCriticalCount={nonCriticalCount}
                                        hasRefinementsPanel={filtersOpen}
                                        isLoading={isProductGridLoading}
                                        handleProductClick={handleProductClick}
                                        topCategoryName={
                                            category.parentCategoryTree?.find((p) => p.id !== 'root')?.name ??
                                            category.name
                                        }
                                        errorElement={<ProductGridError />}
                                    />
                                )}
                            </UITarget>

                            {!isProductGridLoading &&
                                (!isRestoring || loadMoreError) &&
                                (isLoadMoreMode ? (
                                    <div className="mt-10">
                                        <LoadMore
                                            loadedCount={loadedCount}
                                            total={loadMoreTotal}
                                            hasMore={hasMore}
                                            capReached={capReached}
                                            isLoading={isLoadingMore}
                                            hasError={loadMoreError}
                                            onLoadMore={loadMore}
                                        />
                                    </div>
                                ) : (
                                    searchResultCritical.total > 1 && (
                                        <div className="mt-10">
                                            <CategoryPagination
                                                limit={limit}
                                                offset={searchResultCritical.offset}
                                                total={searchResultCritical.total}
                                            />
                                        </div>
                                    )
                                ))}
                        </div>
                    </div>
                </div>
            </div>
            <Suspense fallback={null}>
                <CategoryJsonLd categorySchemaPromise={categorySchema} />
            </Suspense>
        </>
    );
}
